import React, {ChangeEvent} from 'react';
import {useTranslation} from "react-i18next";
import {MetadataPropertyType} from "../../api/entities/replancity_MetadataProperty";
import {formatNumber} from "../../utils/utils";
import * as TextField from "./TextField/TextField";
import * as ColorPickerField from "./ColorPickerField/ColorPickerField";
import * as CheckboxField from './CheckboxField/CheckboxField';
import {Option} from "./SelectBox/SelectBox";
import * as RoadSegmentField from "./RoadSegmentField/RoadSegmentField";
import * as DrawGeometryField from "./DrawGeometryField/DrawGeometryField";
import PublicTransitVehicleTypeField from "./PublicTransitVehicleTypeField/PublicTransitVehicleTypeField";
import PublicTransitFareField from "./PublicTransitFareField/PublicTransitFareField";
import * as SelectBox from "./SelectBox/SelectBox";
import {EntityFormMetadata} from "../../hooks/entites/useEntityLoader";
import {Row} from "./Form/Form";
import * as FileField from "./FileField/FileField";


type Props = {
    initialValue: any;
    value: any;
    toShowField?: boolean;
    required: boolean;
    metadata: EntityFormMetadata;
    onChange?: (event: ChangeEvent<HTMLElement>) => void;
    autoFocus?: boolean;
    disabled?: boolean;
}

const timeValidator = (event: ChangeEvent<HTMLInputElement>, validationMsg: string) => {
    const {value} = event.target;
    const parts = value.split(':');
    event.target.setCustomValidity(parts.length < 3 ? validationMsg : '');
}

const FieldCreator = React.memo(React.forwardRef(({
                                                      initialValue,
                                                      value,
                                                      toShowField = true,
                                                      required,
                                                      metadata,
                                                      onChange,
                                                      autoFocus,
                                                      disabled
                                                  }: Props, ref: React.ForwardedRef<HTMLElement>) => {
    const {t} = useTranslation();

    const {
        id,
        name,
        printedName,
        type,
        options,
        validationRegex,
        defaultValue
    } = metadata;

    const getField = ({
                          selectOptions,
                          label,
                          type,
                          initialValue,
                          value,
                          defaultValue,
                          onChange,
                          alertLabel,
                          autoFocus,
                          validationRegex,
                          disabled,
                          hidden
                      }: {
                          selectOptions: Option[];
                          label: string;
                          type: MetadataPropertyType;
                          initialValue: any;
                          value: any;
                          defaultValue?: any;
                          onChange?: (event: ChangeEvent<HTMLElement>) => void;
                          alertLabel?: string;
                          autoFocus?: boolean;
                          validationRegex?: string;
                          disabled?: boolean;
                          hidden?: boolean;
                      }
    ) => {
        switch (type) {
            case 'String':
            case 'Project':
            case 'UUID':
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="text"
                    required={required}
                    value={value}
                    defaultValue={defaultValue}
                    onChange={onChange}
                    ref={ref as any}
                    autoFocus={autoFocus}
                    validationRegex={validationRegex}
                    disabled={disabled || hidden}
                    hidden={hidden}
                />
            case 'LocalTime':
            case 'DEPARTURE':
            case 'INTERVAL':
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="time"
                    required={required}
                    value={value}
                    defaultValue={defaultValue}
                    step="1"
                    onChange={(event) => timeValidator(event as ChangeEvent<HTMLInputElement>, t('entity-form.validation-seconds-absent'))}
                    ref={ref as any}
                    testId={name}
                    validationRegex={validationRegex}
                    disabled={disabled || hidden}
                    hidden={hidden}
                />
            case 'Integer':
            case 'Double':
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="text"
                    required={required}
                    value={formatNumber(value)}
                    defaultValue={defaultValue}
                    onChange={onChange}
                    ref={ref as any}
                    validationRegex={validationRegex}
                    disabled={disabled || hidden}
                    hidden={hidden}
                />
            case "Boolean":
                return <CheckboxField.Labeled
                    name={name}
                    checked={!!value && value !== 'false'}
                    alertLabel={alertLabel}
                    label={label}
                    onChange={onChange}
                    required={required}
                    disabled={disabled || hidden}
                />
            case "Color":
                return <ColorPickerField.Labeled
                    name={name}
                    value={value as string}
                    defaultValue={defaultValue}
                    alertLabel={alertLabel}
                    label={label}
                    onChange={onChange}
                    required={required}
                    disabled={disabled || hidden}
                />
            case 'Enum':
                return <SelectBox.Labeled
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    options={options}
                    value={value}
                    defaultValue={defaultValue}
                    required={required}
                    onChange={onChange}
                    ref={ref}
                    disabled={disabled || hidden}
                />
            case 'PublicTransitFareOptions':
                return <PublicTransitFareField
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    options={selectOptions}
                    value={value}
                    required={required}
                    onChange={onChange}
                    ref={ref as any}
                    disabled={disabled || hidden}
                />
            case 'PublicTransitVehicleType':
                return <PublicTransitVehicleTypeField
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    options={selectOptions}
                    value={value}
                    required={required}
                    onChange={onChange}
                    disabled={disabled || hidden}
                />
            case 'Point':
            case 'LineString':
            case 'Polygon':
                return <DrawGeometryField.Labeled
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    initialValue={initialValue}
                    value={value}
                    drawMode={type}
                    required={required}
                    onChange={onChange}
                    disabled={disabled || hidden}
                />
            case 'RoadInfrastructureSegment':
                return <RoadSegmentField.Labeled
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    value={value}
                    required={required}
                    onChange={onChange}
                    disabled={disabled || hidden}
                />
            case 'File':
                return <FileField.Labeled
                    label={label}
                    name={name}
                    required={required}
                    onChange={onChange}
                    disabled={disabled || hidden}
                />
            default:
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="text"
                    required={required}
                    value={value}
                    defaultValue={defaultValue}
                    onChange={onChange}
                    ref={ref as any}
                    autoFocus={autoFocus}
                    validationRegex={validationRegex}
                    disabled={disabled || hidden}
                    hidden={hidden}
                />
        }
    }

    return (
        <Row key={id} hidden={!toShowField}>
            {getField({
                selectOptions: options,
                label: printedName,
                type,
                initialValue,
                value,
                defaultValue,
                onChange,
                autoFocus,
                validationRegex,
                disabled,
                hidden: !toShowField
            })}
        </Row>
    )
}));

export default FieldCreator;